import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { expensesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, MagnifyingGlass, Paperclip, PencilSimple, Trash, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const Expenses = () => {
  const { t, language } = useLanguage();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const [formData, setFormData] = useState({
    expense_type: 'school',
    designation: '',
    amount: '',
    date: new Date().toISOString().slice(0, 10)
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [removeReceipt, setRemoveReceipt] = useState(false);
  const [reconciliation, setReconciliation] = useState(null);
  const [byMonth, setByMonth] = useState([]);

  const expenseTypes = [
    { value: 'school', label: language === 'fr' ? 'Scolarité' : 'Tuition' },
    { value: 'registration', label: language === 'fr' ? 'Inscription / Réinscription' : 'Registration' },
    { value: 'canteen', label: language === 'fr' ? 'Cantine' : 'Canteen' },
    { value: 'transport', label: language === 'fr' ? 'Transport' : 'Transport' }
  ];

  useEffect(() => {
    loadExpenses();
  }, []);

  const loadExpenses = async () => {
    try {
      const [exp, recon, summ] = await Promise.all([
        expensesApi.getAll(),
        expensesApi.reconciliation(),
        expensesApi.summary(),
      ]);
      setExpenses(exp.data);
      setReconciliation(recon.data);
      setByMonth(summ.data?.by_month || []);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    window.open(expensesApi.exportUrl(), '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (receiptFile && receiptFile.size > 2 * 1024 * 1024) {
      toast.error(language === 'fr' ? 'Le justificatif ne doit pas dépasser 2 Mo' : 'Receipt must be ≤ 2 MB');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('expense_type', formData.expense_type);
      fd.append('designation', formData.designation);
      fd.append('amount', String(parseFloat(formData.amount)));
      fd.append('date', formData.date);
      if (receiptFile) fd.append('receipt', receiptFile);
      if (editingId) {
        if (removeReceipt && !receiptFile) fd.append('remove_receipt', 'true');
        await expensesApi.update(editingId, fd);
      } else {
        await expensesApi.create(fd);
      }
      toast.success(t('success'));
      setDialogOpen(false);
      resetForm();
      loadExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const openEdit = (expense) => {
    setEditingId(expense.id);
    setEditingExpense(expense);
    setRemoveReceipt(false);
    setReceiptFile(null);
    setFormData({
      expense_type: expense.expense_type,
      designation: expense.designation,
      amount: String(expense.amount),
      date: expense.date,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (expense) => {
    if (!window.confirm(language === 'fr'
      ? `Supprimer la dépense « ${expense.designation} » ?`
      : `Delete expense "${expense.designation}"?`)) return;
    try {
      await expensesApi.remove(expense.id);
      toast.success(t('success'));
      loadExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setEditingExpense(null);
    setRemoveReceipt(false);
    setFormData({
      expense_type: 'school',
      designation: '',
      amount: '',
      date: new Date().toISOString().slice(0, 10)
    });
    setReceiptFile(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  const filteredExpenses = expenses.filter(e => 
    filterType === 'all' || e.expense_type === filterType
  );

  const totalByType = expenses.reduce((acc, e) => {
    acc[e.expense_type] = (acc[e.expense_type] || 0) + (e.amount || 0);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">{t('expenses')}</h1>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleExportExcel}
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="export-expenses-btn"
          >
            <DownloadSimple size={18} className="mr-2" />
            {language === 'fr' ? 'Export Excel' : 'Export Excel'}
          </Button>
          <Button
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-[#0052FF] hover:bg-[#0043D1]"
            data-testid="add-expense-btn"
          >
            <Plus size={18} className="mr-2" />
            {t('addExpense')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {expenseTypes.map(type => (
          <Card key={type.value} className="border border-[#E2E8F0] shadow-none">
            <CardContent className="p-4">
              <p className="font-manrope text-sm text-[#64748B]">{type.label}</p>
              <p className="font-outfit text-xl font-bold text-[#0F172A]">
                {formatCurrency(totalByType[type.value] || 0)}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Rapprochement de caisse (recettes vs dépenses) */}
      {reconciliation && (
        <Card className="border border-[#E2E8F0] shadow-none" data-testid="reconciliation-card">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
              <h2 className="font-outfit text-lg font-bold text-[#0F172A]">
                {language === 'fr' ? 'Rapprochement de caisse' : 'Cash reconciliation'}
              </h2>
              <div className="text-right">
                <p className="text-xs text-[#64748B] font-manrope">{language === 'fr' ? 'Solde de caisse' : 'Cash balance'}</p>
                <p className={`font-outfit text-2xl font-bold ${reconciliation.solde_caisse >= 0 ? 'text-emerald-600' : 'text-[#EF4444]'}`} data-testid="cash-balance">
                  {formatCurrency(reconciliation.solde_caisse)}
                </p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-manrope">
                <thead>
                  <tr className="text-left text-[#64748B] border-b border-[#E2E8F0]">
                    <th className="py-2 pr-4 font-semibold">{language === 'fr' ? 'Poste' : 'Category'}</th>
                    <th className="py-2 px-4 font-semibold text-right">{language === 'fr' ? 'Recettes' : 'Revenue'}</th>
                    <th className="py-2 px-4 font-semibold text-right">{language === 'fr' ? 'Dépenses' : 'Expenses'}</th>
                    <th className="py-2 pl-4 font-semibold text-right">{language === 'fr' ? 'Solde' : 'Balance'}</th>
                  </tr>
                </thead>
                <tbody>
                  {reconciliation.postes.map(p => (
                    <tr key={p.key} className="border-b border-[#F1F5F9]">
                      <td className="py-2 pr-4">{p.label}</td>
                      <td className="py-2 px-4 text-right text-emerald-600">{formatCurrency(p.recettes)}</td>
                      <td className="py-2 px-4 text-right text-[#EF4444]">{formatCurrency(p.depenses)}</td>
                      <td className={`py-2 pl-4 text-right font-semibold ${p.solde >= 0 ? 'text-[#0F172A]' : 'text-[#EF4444]'}`}>{formatCurrency(p.solde)}</td>
                    </tr>
                  ))}
                  <tr className="font-bold text-[#0F172A] bg-[#F8FAFC]">
                    <td className="py-2 pr-4">{language === 'fr' ? 'TOTAL' : 'TOTAL'}</td>
                    <td className="py-2 px-4 text-right text-emerald-600">{formatCurrency(reconciliation.total_recettes)}</td>
                    <td className="py-2 px-4 text-right text-[#EF4444]">{formatCurrency(reconciliation.total_depenses)}</td>
                    <td className={`py-2 pl-4 text-right ${reconciliation.solde_caisse >= 0 ? 'text-emerald-600' : 'text-[#EF4444]'}`}>{formatCurrency(reconciliation.solde_caisse)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dépenses par mois */}
      {byMonth.length > 0 && (
        <Card className="border border-[#E2E8F0] shadow-none" data-testid="by-month-card">
          <CardContent className="p-4 sm:p-6">
            <h2 className="font-outfit text-lg font-bold text-[#0F172A] mb-4">
              {language === 'fr' ? 'Dépenses par mois' : 'Expenses by month'}
            </h2>
            {(() => {
              const max = Math.max(...byMonth.map(m => m.total), 1);
              return (
                <div className="space-y-2">
                  {byMonth.map(m => (
                    <div key={m.month} className="flex items-center gap-3">
                      <span className="w-20 text-xs text-[#64748B] font-manrope shrink-0">{m.month}</span>
                      <div className="flex-1 h-5 bg-[#F1F5F9] rounded-full overflow-hidden">
                        <div className="h-full bg-[#0052FF] rounded-full" style={{ width: `${(m.total / max) * 100}%` }} />
                      </div>
                      <span className="w-32 text-xs text-right font-manrope font-semibold text-[#0F172A] shrink-0">{formatCurrency(m.total)}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-4">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[200px] border-[#E2E8F0]" data-testid="filter-expense-type">
              <SelectValue placeholder={language === 'fr' ? 'Type de dépense' : 'Expense Type'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all')}</SelectItem>
              {expenseTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#E2E8F0]">
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('date')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{language === 'fr' ? 'Type' : 'Type'}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A]">{t('designation')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A] text-right">{t('amount')}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A] text-center">{language === 'fr' ? 'Justificatif' : 'Receipt'}</TableHead>
                <TableHead className="font-outfit font-semibold text-[#0F172A] text-center">{language === 'fr' ? 'Actions' : 'Actions'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0052FF] mx-auto" />
                  </TableCell>
                </TableRow>
              ) : filteredExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell>
                </TableRow>
              ) : (
                filteredExpenses.map((expense) => (
                  <TableRow key={expense.id} className="border-[#E2E8F0] hover:bg-[#F1F5F9]">
                    <TableCell className="font-manrope">{expense.date}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium
                        ${expense.expense_type === 'school' ? 'bg-blue-100 text-blue-700' :
                          expense.expense_type === 'registration' ? 'bg-purple-100 text-purple-700' :
                          expense.expense_type === 'canteen' ? 'bg-green-100 text-green-700' :
                          'bg-yellow-100 text-yellow-700'}`}
                      >
                        {expenseTypes.find(t => t.value === expense.expense_type)?.label || expense.expense_type}
                      </span>
                    </TableCell>
                    <TableCell className="font-manrope">{expense.designation}</TableCell>
                    <TableCell className="font-manrope font-semibold text-right text-[#EF4444]">
                      -{formatCurrency(expense.amount)}
                    </TableCell>
                    <TableCell className="text-center">
                      {expense.has_receipt ? (
                        <a
                          href={expensesApi.receiptUrl(expense.id)}
                          target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[#0052FF] hover:underline text-xs font-medium"
                          data-testid={`expense-receipt-${expense.id}`}
                        >
                          <Paperclip size={14} /> {language === 'fr' ? 'Voir' : 'View'}
                        </a>
                      ) : (
                        <span className="text-[#CBD5E1] text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => openEdit(expense)}
                          className="h-8 w-8 p-0 text-[#64748B] hover:text-[#0052FF]"
                          data-testid={`expense-edit-${expense.id}`}
                          title={language === 'fr' ? 'Modifier' : 'Edit'}
                        >
                          <PencilSimple size={16} />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleDelete(expense)}
                          className="h-8 w-8 p-0 text-[#64748B] hover:text-[#EF4444]"
                          data-testid={`expense-delete-${expense.id}`}
                          title={language === 'fr' ? 'Supprimer' : 'Delete'}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Expense Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">
              {editingId ? (language === 'fr' ? 'Modifier la dépense' : 'Edit expense') : t('addExpense')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-manrope">{language === 'fr' ? 'Type de dépense' : 'Expense Type'}</Label>
              <Select 
                value={formData.expense_type} 
                onValueChange={(v) => setFormData({ ...formData, expense_type: v })}
              >
                <SelectTrigger className="border-[#E2E8F0]" data-testid="expense-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {expenseTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('designation')}</Label>
              <Input
                value={formData.designation}
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                required
                className="border-[#E2E8F0]"
                data-testid="expense-designation"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('amount')} (FCFA)</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                className="border-[#E2E8F0]"
                data-testid="expense-amount"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">{t('date')}</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="border-[#E2E8F0]"
                data-testid="expense-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-manrope">
                {language === 'fr' ? 'Justificatif (pièce comptable)' : 'Receipt'}
                <span className="ml-1 text-xs font-normal text-[#94A3B8]">— {language === 'fr' ? 'max 2 Mo' : 'max 2 MB'}</span>
              </Label>
              <Input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                className="border-[#E2E8F0]"
                data-testid="expense-receipt"
              />
              {receiptFile && (
                <p className="text-xs text-[#64748B]">
                  {receiptFile.name} — {(receiptFile.size / 1024).toFixed(0)} Ko
                </p>
              )}
              {editingId && editingExpense?.has_receipt && !receiptFile && (
                <div className="flex items-center justify-between rounded-md bg-[#F8FAFC] border border-[#E2E8F0] px-3 py-2">
                  <a
                    href={expensesApi.receiptUrl(editingId)}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[#0052FF] hover:underline text-xs font-medium"
                  >
                    <Paperclip size={14} /> {language === 'fr' ? 'Justificatif actuel' : 'Current receipt'}
                  </a>
                  <label className="flex items-center gap-1.5 text-xs text-[#EF4444] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={removeReceipt}
                      onChange={(e) => setRemoveReceipt(e.target.checked)}
                      data-testid="expense-remove-receipt"
                    />
                    {language === 'fr' ? 'Supprimer' : 'Remove'}
                  </label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
              <Button type="submit" className="bg-[#0052FF] hover:bg-[#0043D1]" data-testid="save-expense-btn">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Expenses;
